import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Package, Search, Plus, AlertTriangle, X, Loader2,
  ChevronLeft, ChevronRight, ArrowDown, ArrowUp, MapPin,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost, apiPatch } from '@/lib/api'
import { cn, formatCurrency, formatDate } from '@/lib/utils'

interface InventoryItem {
  id: string
  name: string
  partNumber: string | null
  description: string | null
  category: string | null
  unit: string
  quantityInStock: number
  minStockLevel: number
  reorderQty: number | null
  unitCostGbp: number | null
  storageLocation: string | null
  supplierPartRef: string | null
  lastStocktakeAt: string | null
  isLowStock: boolean
  isOutOfStock: boolean
  stockValue: number
  assetLinks: { asset: { id: string; name: string; code: string } }[]
  _count: { usageRecords: number }
}

interface InventorySummary {
  totalItems: number
  lowStock: number
  outOfStock: number
  totalStockValue: number
}

export function InventoryPage() {
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [lowStockOnly, setLowStockOnly] = useState(false)
  const [page, setPage] = useState(1)

  const [showAddModal, setShowAddModal] = useState(false)
  const [showUseModal, setShowUseModal] = useState<string | null>(null)
  const [showRestockModal, setShowRestockModal] = useState<string | null>(null)

  // Add form
  const [form, setForm] = useState({
    name: '', partNumber: '', description: '', category: '',
    unit: 'ea', quantityInStock: '', minStockLevel: '', reorderQty: '',
    unitCostGbp: '', storageLocation: '', supplierPartRef: '',
  })

  // Use form
  const [useQty, setUseQty] = useState('')
  const [useNotes, setUseNotes] = useState('')

  // Restock form
  const [restockQty, setRestockQty] = useState('')
  const [restockCost, setRestockCost] = useState('')

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['inventory', pid, search, filterCategory, lowStockOnly, page],
    queryFn: () =>
      apiGet<{ data: InventoryItem[]; pagination: any; summary: InventorySummary }>(
        `/properties/${pid}/inventory`,
        { page, limit: 50, ...(search && { search }), ...(filterCategory && { category: filterCategory }), lowStockOnly }
      ),
    enabled: !!pid,
  })

  const { data: categories } = useQuery({
    queryKey: ['inventory-categories', pid],
    queryFn: () => apiGet<string[]>(`/properties/${pid}/inventory/categories`),
    enabled: !!pid,
  })

  // ── Mutations ───────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/properties/${pid}/inventory`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
      setShowAddModal(false)
      resetForm()
    },
  })

  const useMutation2 = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/properties/${pid}/inventory/use`, body),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
      setShowUseModal(null)
      setUseQty('')
      setUseNotes('')
      if (result.lowStockAlert) {
        alert(`Low stock alert: ${result.item.name} is now below minimum stock level`)
      }
    },
  })

  const restockMutation = useMutation({
    mutationFn: ({ itemId, ...body }: Record<string, unknown>) =>
      apiPost(`/properties/${pid}/inventory/${itemId}/restock`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
      setShowRestockModal(null)
      setRestockQty('')
      setRestockCost('')
    },
  })

  function resetForm() {
    setForm({ name: '', partNumber: '', description: '', category: '', unit: 'ea', quantityInStock: '', minStockLevel: '', reorderQty: '', unitCostGbp: '', storageLocation: '', supplierPartRef: '' })
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    createMutation.mutate({
      name: form.name,
      ...(form.partNumber && { partNumber: form.partNumber }),
      ...(form.description && { description: form.description }),
      ...(form.category && { category: form.category }),
      unit: form.unit,
      quantityInStock: parseFloat(form.quantityInStock) || 0,
      minStockLevel: parseFloat(form.minStockLevel) || 0,
      ...(form.reorderQty && { reorderQty: parseFloat(form.reorderQty) }),
      ...(form.unitCostGbp && { unitCostGbp: parseFloat(form.unitCostGbp) }),
      ...(form.storageLocation && { storageLocation: form.storageLocation }),
      ...(form.supplierPartRef && { supplierPartRef: form.supplierPartRef }),
    })
  }

  const items = data?.data ?? []
  const summary = data?.summary
  const pagination = data?.pagination

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Inventory & Parts</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track stock levels and parts usage</p>
        </div>
        <button onClick={() => { resetForm(); setShowAddModal(true) }} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="card py-4">
            <p className="text-xs text-gray-400">Total Items</p>
            <p className="text-2xl font-bold text-navy-800">{summary.totalItems}</p>
          </div>
          <div className="card py-4 border-amber-200">
            <p className="text-xs text-gray-400">Low Stock</p>
            <p className={cn('text-2xl font-bold', summary.lowStock > 0 ? 'text-amber-600' : 'text-navy-800')}>
              {summary.lowStock}
            </p>
          </div>
          <div className="card py-4 border-red-200">
            <p className="text-xs text-gray-400">Out of Stock</p>
            <p className={cn('text-2xl font-bold', summary.outOfStock > 0 ? 'text-red-600' : 'text-navy-800')}>
              {summary.outOfStock}
            </p>
          </div>
          <div className="card py-4">
            <p className="text-xs text-gray-400">Total Stock Value</p>
            <p className="text-2xl font-bold text-navy-800">{formatCurrency(summary.totalStockValue)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search parts by name or part number..."
              className="input pl-9"
            />
          </div>
          <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1) }} className="input w-auto min-w-[150px]">
            <option value="">All Categories</option>
            {categories?.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={() => { setLowStockOnly(!lowStockOnly); setPage(1) }}
            className={cn('btn-secondary text-xs', lowStockOnly && 'border-amber-400 text-amber-600 bg-amber-50')}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Low stock only
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-navy-800" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No inventory items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Item</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Location</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-500">In Stock</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-500">Min Level</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Unit Cost</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Value</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(item => (
                  <tr key={item.id} className={cn(
                    'hover:bg-gray-50/50 transition-colors',
                    item.isOutOfStock && 'bg-red-50/30',
                    item.isLowStock && !item.isOutOfStock && 'bg-amber-50/20',
                  )}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-navy-800">{item.name}</p>
                        {item.partNumber && <p className="text-xs text-gray-400 font-mono">{item.partNumber}</p>}
                        {item.assetLinks.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {item.assetLinks.slice(0, 2).map(l => (
                              <span key={l.asset.id} className="badge bg-gray-100 text-gray-500 text-xs">{l.asset.code}</span>
                            ))}
                            {item.assetLinks.length > 2 && (
                              <span className="text-xs text-gray-400">+{item.assetLinks.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.category ?? '—'}</td>
                    <td className="px-4 py-3">
                      {item.storageLocation ? (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{item.storageLocation}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn('font-semibold',
                        item.isOutOfStock ? 'text-red-600' : item.isLowStock ? 'text-amber-600' : 'text-navy-800'
                      )}>
                        {item.quantityInStock} {item.unit}
                      </span>
                      {item.isOutOfStock && <p className="text-xs text-red-500">Out of stock</p>}
                      {item.isLowStock && !item.isOutOfStock && <p className="text-xs text-amber-500">Low stock</p>}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500">{item.minStockLevel} {item.unit}</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {item.unitCostGbp ? formatCurrency(item.unitCostGbp) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {item.stockValue > 0 ? formatCurrency(item.stockValue) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => setShowUseModal(item.id)}
                          className="btn-secondary py-1 px-2 text-xs"
                          title="Use stock"
                        >
                          <ArrowUp className="w-3.5 h-3.5" /> Use
                        </button>
                        <button
                          onClick={() => setShowRestockModal(item.id)}
                          className="btn-secondary py-1 px-2 text-xs"
                          title="Restock"
                        >
                          <ArrowDown className="w-3.5 h-3.5" /> Restock
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800">Add Inventory Item</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="label">Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input" required placeholder="e.g. AHU Belt - V-Belt A68" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Part Number</label>
                  <input value={form.partNumber} onChange={e => setForm({ ...form, partNumber: e.target.value })} className="input" placeholder="e.g. VB-A68" />
                </div>
                <div>
                  <label className="label">Category</label>
                  <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="input" placeholder="e.g. HVAC Parts" list="inv-cats" />
                  <datalist id="inv-cats">{categories?.map(c => <option key={c} value={c} />)}</datalist>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Unit</label>
                  <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="input">
                    <option value="ea">Each</option><option value="m">Metres</option><option value="L">Litres</option><option value="kg">Kilograms</option><option value="box">Box</option><option value="roll">Roll</option>
                  </select>
                </div>
                <div>
                  <label className="label">Qty in Stock</label>
                  <input type="number" step="0.1" value={form.quantityInStock} onChange={e => setForm({ ...form, quantityInStock: e.target.value })} className="input" placeholder="0" />
                </div>
                <div>
                  <label className="label">Min Level</label>
                  <input type="number" step="0.1" value={form.minStockLevel} onChange={e => setForm({ ...form, minStockLevel: e.target.value })} className="input" placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Unit Cost (GBP)</label>
                  <input type="number" step="0.01" value={form.unitCostGbp} onChange={e => setForm({ ...form, unitCostGbp: e.target.value })} className="input" placeholder="0.00" />
                </div>
                <div>
                  <label className="label">Storage Location</label>
                  <input value={form.storageLocation} onChange={e => setForm({ ...form, storageLocation: e.target.value })} className="input" placeholder="Store Room A, Shelf 3" />
                </div>
              </div>
              <div>
                <label className="label">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="input resize-none" placeholder="Optional description..." />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={!form.name || createMutation.isPending} className="btn-primary">
                  {createMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</> : <><Plus className="w-4 h-4" /> Add Item</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Use Stock Modal */}
      {showUseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-navy-800 mb-4">Use Stock</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Quantity Used *</label>
                <input type="number" step="0.1" min="0.1" value={useQty} onChange={e => setUseQty(e.target.value)} className="input" required />
              </div>
              <div>
                <label className="label">Notes</label>
                <input value={useNotes} onChange={e => setUseNotes(e.target.value)} className="input" placeholder="e.g. Used on WO-2026-000012" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowUseModal(null); setUseQty(''); setUseNotes('') }} className="btn-secondary">Cancel</button>
                <button
                  onClick={() => useMutation2.mutate({ inventoryItemId: showUseModal, quantityUsed: parseFloat(useQty), notes: useNotes || undefined })}
                  disabled={!useQty || useMutation2.isPending}
                  className="btn-primary"
                >
                  {useMutation2.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />} Use
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restock Modal */}
      {showRestockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-navy-800 mb-4">Restock</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Quantity to Add *</label>
                <input type="number" step="0.1" min="0.1" value={restockQty} onChange={e => setRestockQty(e.target.value)} className="input" required />
              </div>
              <div>
                <label className="label">New Unit Cost (GBP)</label>
                <input type="number" step="0.01" value={restockCost} onChange={e => setRestockCost(e.target.value)} className="input" placeholder="Leave blank to keep current" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowRestockModal(null); setRestockQty(''); setRestockCost('') }} className="btn-secondary">Cancel</button>
                <button
                  onClick={() => restockMutation.mutate({ itemId: showRestockModal, quantity: parseFloat(restockQty), ...(restockCost && { unitCostGbp: parseFloat(restockCost) }) })}
                  disabled={!restockQty || restockMutation.isPending}
                  className="btn-primary"
                >
                  {restockMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDown className="w-4 h-4" />} Restock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
